import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';

import type { IntlShape, WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics/types/enums';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import { MediaInlineImageCard } from '@atlaskit/editor-common/media-inline-image-card';
import type { MediaInlineAttrs } from '@atlaskit/editor-common/media-inline/types';
import { useProvider } from '@atlaskit/editor-common/provider-factory/context';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory/context-identifier-provider';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { Mark } from '@atlaskit/editor-prosemirror/model';
import MediaInlineCard from '@atlaskit/media-card/loader';
import type { InlineCardEvent } from '@atlaskit/media-card/types';
import { getMediaClient } from '@atlaskit/media-client-react/get-media-client';
import { MediaClientContext } from '@atlaskit/media-client-react/media-client-provider';
import type { FileIdentifier } from '@atlaskit/media-client/identifier';
import type { MediaFeatureFlags } from '@atlaskit/media-common/types';
import type { FileState } from '@atlaskit/media-state/file-state';
import { MediaInlineCardLoadingView } from '@atlaskit/media-ui/LoadingView';

import { ACTION_SUBJECT } from '../../analytics/enums';
import type { MediaSSR } from '../../types/mediaOptions';
import type { ClipboardAttrs } from '../../ui/MediaCard';
import { getClipboardAttrs, mediaIdentifierMap } from '../../ui/MediaCard';
import { ErrorBoundary } from '../../ui/Renderer/ErrorBoundary';
import type { RendererAppearance } from '../../ui/Renderer/types';
import type { RendererContext } from '../types';

type RenderMediaInlineProps = {
	children?: React.ReactNode;
	clipboardAttrs: ClipboardAttrs;
	collection?: string;
	eventHandlers?: EventHandlers;
	fallbackMediaNameFetcher?: (id: string) => Promise<string>;
	featureFlags?: MediaFeatureFlags;
	identifier: FileIdentifier;
	intl?: IntlShape;
	rendererAppearance?: RendererAppearance;
	rendererContext?: RendererContext;
	ssr?: MediaSSR;
};

type MediaInlineProps = {
	collection?: string;
	eventHandlers?: EventHandlers;
	fallbackMediaNameFetcher?: (id: string) => Promise<string>;
	featureFlags?: MediaFeatureFlags;
	id: string;
	marks?: Array<Mark>;
	providers: ProviderFactory;
	rendererAppearance?: RendererAppearance;
	ssr?: MediaSSR;
};

const RenderMediaInline = ({
	rendererAppearance: _rendererAppearance,
	clipboardAttrs,
	collection: collectionName,
	eventHandlers,
	identifier,
	fallbackMediaNameFetcher,
	ssr,
}: RenderMediaInlineProps) => {
	const [contextIdentifier, setContextIdentifier] = useState<
		ContextIdentifierProvider | undefined
	>();

	const [fileState, setFileState] = useState<FileState | undefined>();

	const mediaClient = useContext(MediaClientContext);
	const contextIdentifierProvider = useProvider('contextIdentifierProvider');

	const ssrMediaItem = useMemo(
		() => ssr?.ssrMediaItems?.find((item) => item.id === identifier.id),
		[ssr?.ssrMediaItems, identifier.id],
	);

	useEffect(() => {
		if (contextIdentifierProvider) {
			contextIdentifierProvider.then((resolvedContextID) => {
				if (contextIdentifier !== resolvedContextID) {
					setContextIdentifier(resolvedContextID);
				}
			});
		}
	}, [contextIdentifier, contextIdentifierProvider]);

	const updateFileState = useCallback(
		async (id: string) => {
			const options = {
				collectionName,
			};
			try {
				if (mediaClient) {
					const fileState = await mediaClient.file.getCurrentState(id, options);
					setFileState(fileState);
				}
			} catch (error) {
				// eslint-disable-line no-unused-vars
				// do not set state on error
			}
		},
		[collectionName, mediaClient],
	);

	useEffect(() => {
		const { id } = identifier;
		const nodeIsInCache = id && mediaIdentifierMap.has(id);
		if (!nodeIsInCache) {
			mediaIdentifierMap.set(identifier.id as string, {
				...identifier,
				collectionName,
			});
		}
		return () => {
			mediaIdentifierMap.delete(id);
		};
	}, [identifier, collectionName]);

	useEffect(() => {
		const { id } = clipboardAttrs;
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
		id && updateFileState(id);
	}, [contextIdentifier, clipboardAttrs, updateFileState]);

	/*
	 * Only show the loading view if the media client is not ready
	 * prevents calling the media API before the provider is ready
	 */
	if (!mediaClient) {
		return <MediaInlineCardLoadingView message="" isSelected={false} />;
	}

	const handleMediaInlineClick = (result: InlineCardEvent) => {
		if (eventHandlers?.media?.onClick) {
			eventHandlers?.media?.onClick(result);
		}
	};

	const shouldOpenMediaViewer = true;
	const shouldDisplayToolTip = true;
	const { id, collection } = clipboardAttrs;
	return (
		// eslint-disable-next-line @atlaskit/design-system/prefer-primitives
		<span
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...getClipboardAttrs({
				id,
				collection,
				contextIdentifierProvider: contextIdentifier,
				fileState,
			})}
			data-node-type="mediaInline"
		>
			<MediaInlineCard
				identifier={identifier}
				onClick={handleMediaInlineClick}
				shouldOpenMediaViewer={shouldOpenMediaViewer}
				shouldDisplayToolTip={shouldDisplayToolTip}
				mediaClientConfig={mediaClient.mediaClientConfig}
				mediaViewerItems={Array.from(mediaIdentifierMap.values())}
				fallbackMediaNameFetcher={fallbackMediaNameFetcher}
				ssrMediaItem={ssrMediaItem}
			/>
		</span>
	);
};

const MediaInline = (props: MediaInlineProps & WrappedComponentProps & MediaInlineAttrs) => {
	const {
		collection,
		id,
		intl,
		rendererAppearance,
		featureFlags,
		type: fileType,
		alt,
		width,
		height,
		marks,
		ssr,
		fallbackMediaNameFetcher,
	} = props;

	const clipboardAttrs: ClipboardAttrs = {
		id,
		collection,
	};

	const identifier: FileIdentifier = {
		id,
		mediaItemType: 'file',
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		collectionName: collection!,
	};

	const mediaClient = useContext(MediaClientContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (fileType === 'image') {
		const hasLinkMark = marks?.find((mark) => mark.type.name === 'link');
		const borderMark = marks?.find((mark) => mark?.type.name === 'border');
		const borderColor = borderMark?.attrs.color ?? '';
		const borderSize = borderMark?.attrs.size ?? 0;

		return (
			<ErrorBoundary
				component={ACTION_SUBJECT.RENDERER}
				componentId={ACTION_SUBJECT_ID.MEDIA_INLINE_IMAGE}
				createAnalyticsEvent={createAnalyticsEvent}
			>
				<MediaInlineImageCard
					mediaClient={ssr?.config ? getMediaClient(ssr.config) : mediaClient}
					identifier={identifier}
					alt={alt}
					width={width}
					height={height}
					ssr={ssr}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					border={{ borderSize, borderColor }}
					serializeDataAttrs
					shouldOpenMediaViewer={!hasLinkMark}
				/>
			</ErrorBoundary>
		);
	}

	return (
		<RenderMediaInline
			identifier={identifier}
			clipboardAttrs={clipboardAttrs}
			eventHandlers={props.eventHandlers}
			rendererAppearance={rendererAppearance}
			intl={intl}
			collection={collection}
			featureFlags={featureFlags}
			fallbackMediaNameFetcher={fallbackMediaNameFetcher}
			ssr={ssr}
		/>
	);
};

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const _default_1: React.FC<
	WithIntlProps<MediaInlineProps & WrappedComponentProps & MediaInlineAttrs>
> & {
	WrappedComponent: React.ComponentType<
		MediaInlineProps & WrappedComponentProps & MediaInlineAttrs
	>;
} = injectIntl(MediaInline);
export default _default_1;
